import React, { SyntheticEvent, useContext, useState } from "react";

import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  tableCellClasses,
} from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { Coupon } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/coupons_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { Role } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import useDebouncedEffect from "../../../../helpers/hooks/use-debounced-effect";
import SubscriptionAction from "./subscription-action";
import {
  applyCoupon,
  removeCoupon,
  validateCoupon as validateCouponApi,
} from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { DEFAULT_SNACKBAR_DURATION, EventTypeReason } from "constants/index";
import { OrgDataContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import { toUUID } from "helpers/s12-helpers";
import {
  getBillingProviderName,
  getBillingProviderSite,
  getCouponProvider,
} from "helpers/subscriptions";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";
import { UrlParamTypes } from "types/org-display";

const ManageCoupons = () => {
  const classes = mainStyles();
  const { regionCode } = useParams<UrlParamTypes>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openCancelDialog, setOpenCouponsDialog] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponDescription, setCouponDescription] = useState<Coupon>();
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  enum ManageCouponsOptions {
    APPLY = 0,
    REMOVE = 1,
  }
  const [manageCouponSelection, setManageCouponSelection] = useState(
    ManageCouponsOptions[0],
  );

  const { showError } = useContext(SnackbarContext);
  const { orgData, setOrgData } = useContext(OrgDataContext);

  const isButtonDisabled =
    isSubscriptionActionLoading ||
    [SubscriptionType.FREE, SubscriptionType.FREE_ORG].includes(
      orgData.subscription[0].type,
    );

  const billingProviderName = getBillingProviderName(orgData.subscription[0]);
  const billingProviderSite = getBillingProviderSite(orgData.subscription[0]);
  const couponProvider = getCouponProvider(orgData.subscription[0]);

  const validateApplyCouponCodeError = `Either the coupon code entered is invalid or 
  does not exist on the ${billingProviderSite} ${billingProviderName} site.`;

  useDebouncedEffect(
    () => {
      validateCoupon(couponCode);
    },
    1000,
    [couponCode, setCouponCode],
  );

  const handleSubmit = () => {
    switch (manageCouponSelection) {
      case ManageCouponsOptions[0]: {
        return handleApplyCoupon(couponCode);
      }
      case ManageCouponsOptions[1]: {
        return handleRemoveCoupon(couponCode);
      }
      default: {
        return;
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setManageCouponSelection(event.target.value as string);
  };

  const handleClose = (
    event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined,
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  const updateOrgContextCouponsList = (code: string) => {
    const newOrgData = { ...orgData } as Role;
    if (
      newOrgData.subscription &&
      newOrgData.subscription[0].chargify &&
      newOrgData.subscription[0].chargify.couponCodes.indexOf(code) === -1
    ) {
      newOrgData.subscription[0].chargify.couponCodes.push(code);
      setOrgData(newOrgData);
    }
  };

  const removeOrgContextCouponsList = (code: string) => {
    const newOrgData = { ...orgData } as Role;
    if (newOrgData.subscription && newOrgData.subscription[0].chargify) {
      const newCouponsList =
        newOrgData.subscription[0].chargify.couponCodes.filter(
          (e) => e !== code,
        );
      newOrgData.subscription[0].chargify.couponCodes = newCouponsList;
      setOrgData(newOrgData);
    }
  };

  const setDefaultStateValues = () => {
    setShowLoadingSpinner(false);
    setManageCouponSelection(ManageCouponsOptions[0]);
    setCouponCode("");
    setCouponDescription(undefined);
  };

  const validateCoupon = (code: string) => {
    if (code.length > 0) {
      setShowLoadingSpinner(true);
      validateCouponApi(
        {
          code,
          provider: couponProvider,
        },
        callOptionsHeaders(regionCode, orgData.id),
      )
        .then((res) => {
          setCouponDescription(res.coupon);
        })
        .catch(() => {
          setCouponDescription(undefined);
        })
        .finally(() => {
          setShowLoadingSpinner(false);
        });
    }
  };

  const handleApplyCoupon = (code: string) => {
    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);
    validateCoupon(code);

    return applyCoupon(
      {
        code,
        orgId: toUUID(orgData.id),
      },
      callOptionsHeaders(regionCode, orgData.id),
    )
      .then(() => {
        updateOrgContextCouponsList(code);
        setOpenSuccessAlert(true);
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => {
        setDefaultStateValues();
        setIsSubscriptionActionLoading(false);
      });
  };

  const handleRemoveCoupon = (code: string) => {
    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);
    removeCoupon(
      {
        code,
        orgId: toUUID(orgData.id),
      },
      callOptionsHeaders(regionCode, orgData.id),
    )
      .then(() => {
        removeOrgContextCouponsList(code);
        setOpenSuccessAlert(true);
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => {
        setDefaultStateValues();
        setIsSubscriptionActionLoading(false);
      });
  };

  const displayCouponDetails = (coupon: Coupon) => {
    return (
      <TableContainer component={Paper}>
        <Table
          size={"small"}
          sx={{
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "none",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell colSpan={2}>
                <Grid display="flex" alignItems={"center"}>
                  <LocalOfferIcon />
                  <Typography variant="h6" paddingLeft={"10px"}>
                    {coupon.name}
                  </Typography>
                </Grid>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableRow>
            <TableCell>
              <b>Description</b>
            </TableCell>
            <TableCell>{coupon.description}</TableCell>
          </TableRow>
          {coupon.discountPercentage > 0 && (
            <TableRow>
              <TableCell>
                <b>Percentage</b>
              </TableCell>
              <TableCell>{`${coupon.discountPercentage.toPrecision(
                4,
              )}%`}</TableCell>
            </TableRow>
          )}
          {coupon.amountInCents > 0 && (
            <TableRow>
              <TableCell>
                <b>Discounted Amount</b>
              </TableCell>
              <TableCell>{`${coupon.amountInCents / 100}`}</TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell>
              <b>Expiration</b>
            </TableCell>
            <TableCell>
              {coupon.endDate
                ? formatDateTimeDisplay(convertTimestampToDate(coupon.endDate))
                : "Does not expire"}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={{ borderBottom: "none" }}>
              <b>Created At</b>
            </TableCell>
            <TableCell style={{ borderBottom: "none" }}>
              {coupon.startDate
                ? formatDateTimeDisplay(
                    convertTimestampToDate(coupon.startDate),
                  )
                : ""}
            </TableCell>
          </TableRow>
        </Table>
      </TableContainer>
    );
  };

  const displayManageCouponInstructions = () => {
    return (
      <Box
        component={Paper}
        sx={{
          backgroundColor: "info.main",
          padding: "10px 20px 10px 0px",
          marginTop: "10px",
        }}
        data-testid={"manage-coupon-instructions"}
      >
        <ol>
          <li>
            <Typography>
              The coupon must be <b>valid</b> in {billingProviderName} before it
              can be applied.
            </Typography>
          </li>
          <li>
            <Typography>
              The coupon code <b>does not have to be valid</b> in{" "}
              {billingProviderName} when attempting to remove a coupon from the
              subscription.
            </Typography>
          </li>
          <li>
            <Typography>
              Using both options can allow you to manually <b>resync</b> any
              coupon mismatches between the SafetyCulture and{" "}
              {billingProviderName} subscription.
            </Typography>
          </li>
        </ol>
      </Box>
    );
  };

  const validateApplyCouponCheck = () => {
    return (
      couponCode !== "" &&
      manageCouponSelection === ManageCouponsOptions[0] &&
      couponDescription === undefined
    );
  };

  return (
    <>
      <SubscriptionAction
        title="Coupons Applied:"
        middleElem={orgData.subscription[0].chargify?.couponCodes.join(", ")}
      >
        <Button
          sx={{ mt: 1, mr: 2 }}
          data-testid="btn-manage-subscription-coupon"
          variant="contained"
          color="primary"
          fullWidth
          disabled={isButtonDisabled}
          onClick={() => setOpenCouponsDialog(true)}
        >
          MANAGE COUPONS
        </Button>
      </SubscriptionAction>

      <ConfirmDialog
        title="Manage Coupons"
        cancelBtnLabel="Cancel"
        confirmBtnLabel={manageCouponSelection}
        open={openCancelDialog}
        setOpen={setOpenCouponsDialog}
        disabled={
          couponCode === "" ||
          (manageCouponSelection === ManageCouponsOptions[0] &&
            couponDescription === undefined)
        }
        onConfirm={() => {
          handleSubmit();
        }}
        onCancel={() => {
          setDefaultStateValues();
        }}
      >
        <Grid
          container
          direction="column"
          justifyContent="flex-start"
          spacing={2}
        >
          <Grid item>{displayManageCouponInstructions()}</Grid>

          <Grid item>
            <TextField
              inputProps={
                { "data-testid": "enter-coupon-code-text-field" } as any
              }
              fullWidth
              margin="dense"
              label="Enter coupon code"
              variant={"outlined"}
              onChange={(e) => {
                setShowLoadingSpinner(true);
                setCouponCode(e.target.value);
              }}
              error={validateApplyCouponCheck()}
              helperText={
                validateApplyCouponCheck() ? validateApplyCouponCodeError : ""
              }
              value={couponCode}
            />
          </Grid>

          {couponCode.length > 0 && showLoadingSpinner ? (
            <Grid
              container
              justifyContent="center"
              className={clsx(classes.spaceTopSmall, classes.spaceRightMedium)}
            >
              <CircularProgress
                color="primary"
                variant="indeterminate"
                thickness={4}
                size={30}
              />
            </Grid>
          ) : null}

          {!showLoadingSpinner && couponDescription && (
            <Grid item>{displayCouponDetails(couponDescription)}</Grid>
          )}

          <Grid item>
            <RadioGroup onChange={handleChange} value={manageCouponSelection}>
              <Grid container direction="column" justifyContent="flex-start">
                <FormControlLabel
                  data-testid="radio-apply-coupon"
                  value={ManageCouponsOptions[0]}
                  control={<Radio />}
                  label="Apply"
                />
                <FormControlLabel
                  data-testid="radio-remove-coupon"
                  value={ManageCouponsOptions[1]}
                  control={<Radio />}
                  label="Remove"
                  disabled={billingProviderName === "Zuora"}
                />
              </Grid>
            </RadioGroup>
          </Grid>
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleClose}
      >
        <Alert severity="success" variant="filled">
          Coupon action successfully performed
        </Alert>
      </Snackbar>
    </>
  );
};

export default ManageCoupons;
